import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { T, ddmon } from "../clientsTokens";
import { Card, CardHead, Secondary } from "./recordBits";
import { BandChip } from "../../../healthcheck/hcTokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/health-check`;
const LABEL = { fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted };

const Cell = ({ label, children, testid }) => (
  <div>
    <div style={LABEL}>{label}</div>
    <div className="text-[13px] font-semibold mt-0.5" data-testid={testid} style={{ color: T.ink }}>
      {children === null || children === undefined || children === "" ? "—" : children}
    </div>
  </div>
);

/* The client record's Health Check block — latest result, trend, and the two
   things staff do from here: invite them, or run one live. */
export const HealthCheckCard = ({ clientId, companyName }) => {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/client/${encodeURIComponent(clientId)}`)).data); }
    catch { /* the card simply stays quiet if it can't load */ }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const latest = d?.latest;
  const trend = (d?.trend || []).slice(-8);
  const max = Math.max(100, ...trend.map((t) => t.score || 0));

  return (
    <Card testid="client-health-check-card">
      <CardHead title="Health Check" count={d?.trend?.length || 0}
        action={<Secondary onClick={() => { window.location.href = `/health-check/${clientId}/staff`; }}
          testid="hc-card-run">Run a check</Secondary>} />
      <div className="px-4 py-3.5">
        {!latest ? (
          <div className="text-[13px]" data-testid="hc-card-empty" style={{ color: T.ink }}>
            No Health Check on record.
            {d?.outstanding
              ? ` Invited ${ddmon(d.invitation_date)} — still waiting on them.`
              : " Invite them, or run one live on a call."}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-3.5">
              <div className="mono" data-testid="hc-card-score"
                style={{ fontSize: 26, fontWeight: 800, color: T.ink, lineHeight: 1 }}>
                {latest.score}<span className="text-[13px]" style={{ color: T.ink3 }}>/100</span>
              </div>
              <BandChip band={latest.final_band} testid="hc-card-band" />
              {latest.calculated_band !== latest.final_band && (
                <span className="text-[11.5px]" style={{ color: T.ink2 }}>
                  calculated {latest.calculated_band}
                </span>
              )}
            </div>
            {latest.override_reason && (
              <div className="text-[12px] mb-3" style={{ color: T.ink2 }}>
                Override: {latest.override_reason}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mb-3.5">
              <Cell label="Tier" testid="hc-card-tier">{d.tier}</Cell>
              <Cell label="Completed by">{latest.completed_by}</Cell>
              <Cell label="Last check">{ddmon(d.last_check_date)}</Cell>
              <Cell label="Next check" testid="hc-card-next">{ddmon(d.next_check_date)}</Cell>
              <Cell label="Software">{latest.breakdown ? `${Math.round(latest.breakdown.software_satisfaction / 6.25) + 1}/5` : "—"}</Cell>
              <Cell label="Recommend">{latest.breakdown ? `${Math.round(latest.breakdown.recommend / 2)}/10` : "—"}</Cell>
              <Cell label="Invited">{ddmon(d.invitation_date)}</Cell>
              <Cell label="Open pain points" testid="hc-card-pains">{d.open_pain_points}</Cell>
            </div>
            {trend.length > 1 && (
              <div className="flex items-end gap-1.5 h-[46px] mb-3" data-testid="hc-card-trend">
                {trend.map((t) => (
                  <div key={t.submission_id} className="flex-1 rounded-t-[3px]" title={`${ddmon(t.date)} — ${t.score}`}
                    style={{ height: `${Math.max(8, (t.score / max) * 100)}%`,
                      background: t.band === "Green" ? "#0E6B3F" : t.band === "Amber" ? "#E0B800" : "#C23A00" }} />
                ))}
              </div>
            )}
            <a href={`/health-checks/${latest.id}`} data-testid="hc-card-open"
              className="text-[12px] font-bold uppercase" style={{ color: T.accentInk, letterSpacing: "0.04em" }}>
              Open the latest submission
            </a>
          </>
        )}
        <div className="flex items-center gap-2 mt-3.5 pt-3.5" style={{ borderTop: `1px solid ${T.dividerRow}` }}>
          <button onClick={() => { window.location.href = `/customer-success/health-checks?invite=${clientId}`; }}
            data-testid="hc-card-invite"
            className="h-8 px-3 rounded-[7px] text-[11.5px] font-bold uppercase"
            style={{ background: T.accent, border: `1px solid ${T.accentEdge}`, color: "#000", letterSpacing: "0.04em" }}>
            {d?.outstanding ? "Resend the invitation" : "Invite them"}
          </button>
          <a href="/customer-success/health-checks" className="text-[11.5px]" style={{ color: T.ink2 }}>
            All Health Checks
          </a>
        </div>
      </div>
    </Card>
  );
};

export default HealthCheckCard;
